/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useMemo, useState, useContext } from "react";
import { useLocation } from "react-router-dom";
import { LanguageContext } from "../../context/LanguageContext";
import { queryVacancy, isVacancyExpired } from "./services/salesforceVacancies";

import JobHeader from "./JobHeader";
import JobProfileCard from "./JobProfileCard";
import StickyApplyBar from "./StickyApplyBar";
import EasyApplyForm from "./EasyApplyForm";

const EasyApplyPage = () => {
  const { appLanguage } = useContext(LanguageContext);
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const id = params.get("id") || localStorage.getItem("id") || null;

  const [job, setJob] = useState(null);
  const [jobExpired, setJobExpired] = useState(false);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  useEffect(() => {
    if (!id) { setLoading(false); return; }
    localStorage.setItem("id", id);

    (async () => {
      try {
        const data = await queryVacancy(id);
        setJob(data);
        setJobExpired(isVacancyExpired(data));
      } catch (e) {
        setErr(e);
      } finally {
        setLoading(false);
      }
    })();
  }, [id]);

  const title = useMemo(() => {
    if (!job) return "";
    return appLanguage === "en" ? job.Position_Name__c : job.Position_Name_AR__c || job.Position_Name__c;
  }, [job, appLanguage]);

  // ---- states ----
  if (loading) {
    return (
      <div className="container my-5">
        <div className="placeholder-glow">
          <h1 className="placeholder col-6" aria-hidden="true">Loading...</h1>
          <p className="placeholder col-3" />
          <div className="placeholder col-12 my-4" style={{ height: 240 }} />
          <div className="placeholder col-8 my-2" />
          <div className="placeholder col-10 my-2" />
        </div>
      </div>
    );
  }
  if (!id) {
    return <div className="container my-5"><div className="alert alert-warning">Missing vacancy id.</div></div>;
  }
  if (err) {
    return <div className="container my-5"><div className="alert alert-danger">Could not load this job. Please try again.</div></div>;
  }
  if (!job) {
    return <div className="container my-5"><div className="alert alert-warning">Job not found.</div></div>;
  }
  if (jobExpired) {
    return (
      <div className="container my-5" dir={appLanguage === "ar" ? "rtl" : "ltr"}>
        <div className="alert alert-warning">This opportunity is no longer accepting applications.</div>
        <JobHeader job={job} title={title} appLanguage={appLanguage} showApply={false} />
      </div>
    );
  }

  // ---- page ----
  return (
    <div className="container my-5" dir={appLanguage === "ar" ? "rtl" : "ltr"}>
      <JobHeader job={job} title={title} appLanguage={appLanguage} />

      {/* Optional hero image: add Image_URL__c if you have it */}
      {job.Image_URL__c ? (
        <div className="my-3">
          <img
            src={job.Image_URL__c}
            alt={title}
            className="img-fluid rounded shadow-sm"
            style={{ maxHeight: 420, objectFit: "cover", width: "100%" }}
          />
        </div>
      ) : null}

      <JobProfileCard job={job} title={title} />

      <section className="card border-0 shadow-sm my-4">
        <div className="card-body">
          <h6 className="fw-semibold mb-3" style={{ color: "#008080" }}>job description:</h6>
          <div
            className="lh-base"
            dangerouslySetInnerHTML={{
              __html: appLanguage === "en"
                ? job.Job_Description__c || ""
                : job.Job_Description_AR__c || job.Job_Description__c || "",
            }}
          />
        </div>
      </section>

      <section className="card border-0 shadow-sm my-4">
        <div className="card-body">
          
          <div
            className="lh-base"
            dangerouslySetInnerHTML={{
              __html: appLanguage === "en"
                ? job.Skills_Competencies__c || ""
                : job.Skills_Competencies_AR__c || job.Skills_Competencies__c || "",
            }}
          />
        </div>
      </section>

      <section className="card border-0 shadow-sm my-4">
        <div className="card-body">
          <h6 className="fw-semibold mb-3" style={{ color: "#008080" }}>about us</h6>
          <div
            className="lh-base"
            dangerouslySetInnerHTML={{
              __html: appLanguage === "en"
                ? job.About_Us__c || ""
                : job.About_Us_AR__c || job.About_Us__c || "",
            }}
          />
        </div>
      </section>

      <div id="apply" className="pt-2" />

      <section className="card border-0 shadow-sm my-5">
        <div className="card-body">
          <EasyApplyForm vacancyId={id} />
        </div>
      </section>

      <StickyApplyBar title={title} />
    </div>
  );
};

export default EasyApplyPage;
